import { keyboardVerticalOffset } from '@utils'
import React, { ReactNode } from 'react'
import { KeyboardAvoidingView, KeyboardAvoidingViewProps, Platform } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

export const KeyboardAwareView = ({
  children,
  isFullScreen = false,
  hasOffset = false,
  offsetTopInset = false,
  behavior,
  androidOnly = false,
}: {
  children: ReactNode
  isFullScreen?: boolean
  // For screens whose ScrollView already avoids the keyboard on iOS with
  // `automaticallyAdjustKeyboardInsets`: iOS and web render the children as-is
  // (no double adjustment), Android gets the edge-to-edge shrink described below.
  androidOnly?: boolean
  hasOffset?: boolean
  // For full-screen screens whose content sits below the status-bar safe area
  // (i.e. inside `<Screen>`, which pads the top by `insets.top`). On iOS,
  // `behavior="padding"` measures the view frame *relative to its parent*, so
  // it under-pads by exactly the top inset and the input ends up partially
  // hidden behind the keyboard. Feeding `insets.top` as keyboardVerticalOffset
  // compensates that offset. Same on Android with `behavior="height"` (Android
  // 16 edge-to-edge, below): without it the view stops one status bar too low
  // and the focused field is half hidden behind the keyboard.
  offsetTopInset?: boolean
  behavior?: KeyboardAvoidingViewProps['behavior']
}) => {
  const insets = useSafeAreaInsets()
  if (androidOnly && Platform.OS !== 'android') return <>{children}</>

  const verticalOffset = offsetTopInset
    ? insets.top
    : hasOffset
      ? keyboardVerticalOffset
      : undefined

  return (
    <KeyboardAvoidingView
      style={isFullScreen && { flex: 1, height: '100%' }}
      // Android 16 (API 36) enforces edge-to-edge app-wide and Google removed the
      // opt-out that made `adjustResize` shrink the window (see styles.xml): the
      // window no longer resizes on its own, so KAV must do it instead. Every usage
      // of this component is `isFullScreen` (wraps header + scrollable content +
      // any footer input), so `behavior="height"` reproduces the old window-resize
      // effect closely: this view's own height shrinks by the keyboard height, its
      // flex children reflow, and a footer input ends up right above the keyboard.
      behavior={behavior ?? (Platform.OS === 'ios' ? 'padding' : 'height')}
      keyboardVerticalOffset={verticalOffset}
    >
      {children}
    </KeyboardAvoidingView>
  )
}
